import { type TabButton } from '@uturn/portal/components/page/tabs';
import { type TFunction } from 'i18next';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export enum ShipmentView {
	Comfortable = 'Comfortable',
	Compact = 'Compact',
	Table = 'Table',
}

export type ShipmentViewTab = {
	title: string;
	value: ShipmentView;
};

const SHIPMENT_VIEW_DEFAULT = ShipmentView.Comfortable;
const SHIPMENT_VIEW_LOCAL_STORAGE_KEY =
	'user.settings.shipments.overview.view_mode';

const isShipmentView = (value: unknown): value is ShipmentView => {
	return Object.values(ShipmentView).includes(value as ShipmentView);
};

const getLastShipmentView = (): ShipmentView => {
	const storedView = localStorage.getItem(SHIPMENT_VIEW_LOCAL_STORAGE_KEY);

	return storedView && isShipmentView(storedView)
		? storedView
		: SHIPMENT_VIEW_DEFAULT;
};

const shipmentViewTabs = (
	t: TFunction<'translation', undefined>,
): ShipmentViewTab[] => [
	{
		title: t('shipments.views.comfortable', {
			defaultValue: 'Comfortable',
		}),
		value: ShipmentView.Comfortable,
	},
	{
		title: t('shipments.views.compact', { defaultValue: 'Compact' }),
		value: ShipmentView.Compact,
	},
	{
		title: t('shipments.views.table', { defaultValue: 'Table' }),
		value: ShipmentView.Table,
	},
];

const useShipmentViews = () => {
	const { t } = useTranslation();
	const [shipmentView, setView] = useState(getLastShipmentView());

	const getShipmentViewTabs = (): TabButton[] =>
		shipmentViewTabs(t).map((view) => {
			return {
				title: view.title,
				onClick: () => {
					localStorage.setItem(SHIPMENT_VIEW_LOCAL_STORAGE_KEY, view.value);
					setView(view.value);
				},
				active: shipmentView === view.value,
			};
		});

	return {
		shipmentView,
		getShipmentViewTabs,
	};
};

export default useShipmentViews;
